import {Button, Dropdown, Icon, Text} from '@/components/atoms'
import {RewardFreeProductFromList} from '@/features/Promotions/components/PromotionConditions/Rewards/RewardFreeProductFromList'
import {PromotionCondition} from '@/types/promotion'
import {COLOR_CODE} from '@/utils/constants/colors'
import {Constants} from '@/utils/constants/commonConstants'
import {RewardTypes} from '@/utils/constants/promotionConstants'
import {FC} from 'react'
import {RewardDiscount} from './RewardDiscount'
import {RewardFreeProducts} from './RewardFreeProducts'
import {RewardGiftItem} from './RewardGiftItem'
import {RewardPoints} from './RewardPoints'
import {useRewardItem} from './useRewardItem'

export type RewardItemProps = {
  promotionCondition: PromotionCondition
  index: number
  ModalComponent?: FC<any>
  // Which reward of the layer this item edits; with individual rewards each buy condition has its own
  rewardIndex?: number
  showDeleteLayer?: boolean
  selectedPromotionCondition?: string
}

export const RewardItem: FC<RewardItemProps> = ({
  promotionCondition,
  index,
  ModalComponent,
  rewardIndex = 0,
  showDeleteLayer = true,
  selectedPromotionCondition,
}) => {
  const {
    rewardOptions,
    rewardType,
    rewardValue,
    selectedDiscountType,
    isMaxDiscountExceeded,
    canDeleteLayer,
    handleSelectedRewardItem,
    handleDeleteLayer,
    handleEditMaxDiscount,
  } = useRewardItem({promotionCondition, rewardIndex})

  const getRewardType = () => {
    switch (rewardType) {
      case RewardTypes.FREE_PRODUCT:
        return (
          <RewardFreeProducts
            index={index}
            rewardIndex={rewardIndex}
            promotionCondition={promotionCondition}
            ModalComponent={ModalComponent}
          />
        )
      case RewardTypes.DISCOUNT:
        return (
          <RewardDiscount
            index={index}
            rewardIndex={rewardIndex}
            promotionCondition={promotionCondition}
            showDynamicPlaceholder
          />
        )
      case RewardTypes.GIFT_ITEM:
        return <RewardGiftItem index={index} rewardIndex={rewardIndex} promotionCondition={promotionCondition} />
      case RewardTypes.POINTS:
        return <RewardPoints index={index} rewardIndex={rewardIndex} promotionCondition={promotionCondition} />
      case RewardTypes.FREE_PRODUCT_FROM_LIST:
        return (
          <RewardFreeProductFromList
            promotionCondition={promotionCondition}
            rewardIndex={rewardIndex}
            selectedPromotionCondition={selectedPromotionCondition}
            ModalComponent={ModalComponent}
          />
        )
      case RewardTypes.PERCENTAGE_DISCOUNT:
        return (
          <RewardDiscount
            promotionCondition={promotionCondition}
            rewardIndex={rewardIndex}
            showDynamicPlaceholder
            discountType={selectedDiscountType}
          />
        )
      case RewardTypes.CASH_DISCOUNT:
        return (
          <RewardDiscount
            promotionCondition={promotionCondition}
            rewardIndex={rewardIndex}
            discountType={selectedDiscountType}
          />
        )
      default:
        return null
    }
  }

  const renderRewardType = () => {
    const rewardTypeComponent = getRewardType()

    if (rewardTypeComponent) {
      if (rewardType === RewardTypes.FREE_PRODUCT_FROM_LIST) {
        return <div className="basis-full">{rewardTypeComponent}</div>
      }
      return (
        <>
          <Text>{','}</Text>
          {rewardTypeComponent}
        </>
      )
    } else {
      return null
    }
  }
  return (
    <>
      <Dropdown
        isJsonWrap={false}
        childList={rewardOptions}
        placeholder={Constants.Select}
        updateValue={handleSelectedRewardItem}
        isClearable={false}
        value={rewardValue}
        autocomplete="off"
        required={true}
        disabled={index > 0 || rewardIndex > 0}
        selection="single"
      />
      {renderRewardType()}
      {showDeleteLayer && canDeleteLayer && (
        <Button
          uiSchema={{
            variant: 'tertiary',
            title: 'Delete layer',
            isDark: true,
            size: 'small',
            className: '',
            isLoading: false,
          }}
          handleButton={handleDeleteLayer}
        />
      )}

      {isMaxDiscountExceeded && (
        <>
          <Text style={{color: COLOR_CODE._D0D0D0, display: 'flex', gap: 3, alignItems: 'center'}} size="small">
            <Icon alt="alt value" icon="info" size="small" />
            Discount % exceeds max
          </Text>
          <Button
            handleButton={handleEditMaxDiscount}
            uiSchema={{
              className: '',
              size: 'small',
              title: 'Edit Max',
              variant: 'tertiary',
              isLoading: false,
              isDark: true,
              iconLeading: 'mode_edit',
            }}
          />
        </>
      )}
    </>
  )
}
